// libs
import { Inject, Injectable, InjectionToken } from "@angular/core";
import { LOCAL_STORAGE, SESSION_STORAGE, StorageService } from 'ngx-webstorage-service';

// models
import { User, CustomFields, pnrDetails, easySearch, ObjectLiteral } from "../../_models/user";
import { Temporal } from '@js-temporal/polyfill';
// app
import {
  PARAMS,
  ROUTING_CODES,
  HELP_OPTION_CODES,
  STORAGE_KEYS,
  UA_SOURCE_CODES,
  GATE_CONSTANTS,
  ENVIRONMENT
} from "../../_constants/app.constants";
import { DOCUMENT } from "@angular/common";
import { CryptoService } from "../crypto/crypto.service";
import { AnalyticsService } from "../sdk/analytics/analytics.service";
import { LoggerService } from "../ui/logger/logger.service";
import { WebexService } from "../sdk/webex/webex.service";
import { ParamMap } from "@angular/router";
import { LANGUAGE, LANGUAGES_SUPPORTED } from 'src/app/_constants/app.constants';
import { AppModule } from "src/app/app.module";
import { ServiceModule } from 'src/app/_services/service.module';
import { timer } from "rxjs";

@Injectable({
  providedIn: ServiceModule,
})
export class SessionService {
  // private
  private window: any;

  pnrDetails: pnrDetails = {
    pnr_no: '',
    last_name: '',
  }
  // public
  userData: User = {
    firstName: "",
    lastName: "",
    phone: {
      code: "",
      number: "",
    },
    connectionType: "",
    smsConsent: "",
  };

  easySearchData: easySearch = {
    lastName: "",
    dateOfBirth: {
      year: "",
      day: "",
      month: "",
    },
    destination: ""
  }

  locationData: CustomFields = {
    region: "",
    "routing-code": "",
    "airport-code": "",
    "gate-number": "",
    "asset-id": "",
    "flight_status": "",
    reason: "",
    stationId: "",
    // 'SessionId': "",
    "flight_no": "",
    pnr_no: "",
    departure_date: "",
    "origin_airport": "",
    "destination-airport": "",
    "sms_consent": "",
    "priority_score": "",
    "key_attributes": "",
    "routed-department-id": "",
    "ua_source": UA_SOURCE_CODES.aodWeb,
    "is_checked_in": "",
    "language": "",
    "auto-message": "",
    "referrer": "",
    "mp-number": "",
    "currentLocation": "",
    "flight-connection-time":"",
    "isSameDayTrip":false,
    "ssr":"",
    "mpStatus":"",
    "callId":"",
    "sessionBandwidth":"",
    "transferEligible":"",
    "chatOnly":""
  };

  isLocationAvailableInParams: boolean = false;
  shouldSubmitPNR: boolean = true;
  isEasyPnrSearch: boolean = false;
  encryptedParam: string = "";
  isLangPreferred: boolean = false;
  showFlightLookupForm: boolean = false;
  flightRebookCurrentComponent: string;
  isEncryptedStringInParams: boolean = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(SESSION_STORAGE_SERVICE) private storage: StorageService,
    @Inject(LOCAL_STORAGE_SERVICE) private localStorage: StorageService,
    private logger: LoggerService,
    private webexService: WebexService,
    private cryptoService: CryptoService,
    private analyticsService: AnalyticsService,
  ) {
    this.window = this.document.defaultView;
    this.window.dataLayer = this.dataLayer.bind(this);


    // this.locationData["routing-code"] = "abc";
    // this.locationData["mp-number"] = "abc";
    // this.locationData = { ...this.locationData, "routing-code": 'efg'}
  }

  /**
   *
   * Gets custom dimensions once they are known
   *
   */
  dataLayer() {
    return {
      user: {}, // any user level attributes can go here, right now not sure we have any
      session: {
        'routing-code': this.locationData['routing-code'], // if we have a routing code, track it
        'airport-code': this.locationData['airport-code'], // origin airport, derived from qr code, or PNR if there if not contained in qr code
        'origin_airport': this.locationData['origin_airport'], // origin airport, derived from PNR if there if not contained in qr code
        'gate-number': this.locationData['gate-number'], // derived from qr code or active flight segment if not contained in QR code
        'destination-airport': this.locationData['destination-airport'], // right now,  from active flight segment if pnr provided, in the future the user will select which segment
        'departure_date': this.locationData['departure_date'],
        'flight_status': this.locationData['flight_status'], // whatever we show in the UI for the badge from the active flight segment \
        'flight-no': this.locationData['flight_no'], // the flight number of the active flight segment if we have it
        'reason': this.locationData['reason'],  // based on the button they press, 'change-seat', 'change-flight', 'doc-check', 'other'
        'region': this.locationData['region'], // region the user selected during doc check (in the future this will be from the api as well)
        'sms_consent': this.locationData['sms_consent'],
        'priority_score': this.locationData['priority_score'],
        'key_attributes': this.locationData['key_attributes'],
        'ua_source': this.locationData['ua_source'],
        'is_checked_id': this.locationData["is_checked_in"],
        "routed-department-id": this.locationData["routed-department-id"],
        'type': this.userData['connectionType'], // 'text', 'video', 'audio'
        'language': this.locationData.language,
        'pnr': this.pnrDetails['pnr_no'],
        'first-name': this.userData['firstName'],
        'auto-message': this.locationData['auto-message'],
        'referrer': this.locationData.referrer
      }
    }
  }



  /**
   *
   * Checks if we are in the doc check flow
   *
   */
  isDocCheck() {
    return this.locationData["routing-code"] === ROUTING_CODES.DOC.CODE;
  }

  /**
   * Checks if the route is GEN Route
   */
  isGenRoute() {
    if (this.locationData["airport-code"] != null &&
      this.locationData["airport-code"] != "") {
      return true;
    }
    return false;
  }

  isBaggageRoute() {
    if (this.locationData['airport-code'] != null && this.locationData['gate-number'] == GATE_CONSTANTS.BAG) {
      return true;
    }
    return false;
  }


  isHawaiiRoute() {
    if (this.locationData['airport-code'] != null && this.locationData['gate-number'] == GATE_CONSTANTS.HAWAII) {
      return true;
    }
    return false;
  }


  /**
   * Sets the reason to the webex custom fields
   * @param reason
   */
  setReason(reason: string): void {
    this.locationData.reason = reason;
    this.logger.debug("Session Service: setReason: ", this.locationData);
  }

  /**
   * Sets the flightStatus to the webex custom fields
   * @param flightStatus
   */
  setFlightStatus(flightStatus: string): void {
    this.locationData["flight_status"] = flightStatus;
    this.logger.debug("Session Service: setFlightStatus: ", this.locationData);
  }

  setAdditionalInfo(userData: User | any) {
    this.userData.phone = userData.phone;
  }
  setMpStatus(mpStatus:string){
    this.locationData["mpStatus"] = mpStatus;
  }
  setCallId(callId:string){
    this.locationData["callId"] = callId;
    this.storage.set('callId', callId);
  }
  setSameDayFlight(departDateTime:string,arrivalDateTime:string){
    if(departDateTime==arrivalDateTime){
      this.locationData["isSameDayTrip"]= true;
    }
    else{
      this.locationData["isSameDayTrip"]= false;
    }
  }
  setFlightConnectionTime(difference:string){
    this.locationData["flight-connection-time"] = difference;
  }
  setTimeDiff(time:string){
    let departDateTime = new Date(time);
    let departDateTimeEpochWithoutOffset = departDateTime.getTime() ;
    let now = Temporal.Now.instant();
    let then = Temporal.Instant.fromEpochMilliseconds(departDateTimeEpochWithoutOffset);
    let diff = now.until(then).toString();
    diff= diff.replace('PT','');
    diff= diff.replace('S','');
    const mins = Number(diff)/60;
    return Math.floor(mins).toString();
  }
  setSSR(ssr:string[]){
    let ssrString = "";
    for(let child of ssr){
      ssrString = ssrString.concat(child,";");
   }
    this.locationData["ssr"] = ssrString;
  }
  /**
   *
   * Sets user info
   *
   */
  setUserInfo(userDetails) {
    this.logger.debug("Session Service: setUserInfo: ", userDetails);
    try {
      this.userData = {
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        phone: {
          code: userDetails.phone.code,
          number: userDetails.phone.number,
        },
        connectionType: userDetails.connectionType,
        smsConsent: userDetails.smsConsent,
      };
      this.storage.set(STORAGE_KEYS.USER_DETAILS, this.userData);
      this.analyticsService.setUserName(userDetails.firstName + ' ' + userDetails.lastName)
    } catch (error) {
      this.logger.warn(error);
    }
  }

  clearUserName() {
    this.userData.firstName = "";
  }

  /**
   *
   * Stores webex events in session
   *
   */
  setChatEvents(events: any) {
    this.logger.debug("Session Service: setChatEvents: ", events);
    try {
      this.storage.set(STORAGE_KEYS.CHAT_EVENTS, events);
    } catch (error) {
      this.logger.warn(error);
    }
  }

  /**
   *
   * Clears webex events from session
   *
   */
  clearChatEvents() {
    this.storage.remove(STORAGE_KEYS.CHAT_EVENTS);
  }

  /**
   *
   * Clears user data from session info
   *
   */
  clearUserData() {
    this.logger.debug("Session Service: clearUserData");
    this.userData = {
      firstName: "",
      lastName: "",
      phone: {
        code: "",
        number: "",
      },
      connectionType: "",
      smsConsent: "",
    };
    this.storage.remove(STORAGE_KEYS.USER_DETAILS);
  }

  clearEasySearchData() {
    this.easySearchData = {
      lastName: "",
      dateOfBirth: {
        year: "",
        day: "",
        month: "",
      },
      destination: ""
    }
  }

  /**
   *
   * Clears all data from session info
   *
   */
  clearSessionStorage() {
    this.logger.debug("Session Service: clearStorageData");
    this.storage.clear();
  }
  /**
   * Clears other session data except for PNR details to make them available for subsequent reloads
   */
  clearStorageData() {
    this.storage.remove(STORAGE_KEYS.CUSTOM_FIELDS);
    this.storage.remove(STORAGE_KEYS.PARAMS);
    this.storage.remove(STORAGE_KEYS.USER_DETAILS);
    this.storage.remove(STORAGE_KEYS.CHAT_EVENTS);
  }

  /**
   *
   * Stores custom fields in session storage
   *
   */
  storeCustomFields(): void {
    this.logger.debug("Session Service: storeCustomFields: ", this.locationData);
    this.storage.set(STORAGE_KEYS.CUSTOM_FIELDS, this.locationData);
  }

  loadCustomFieldsFromStorage(): void {
    if (this.storage.get(STORAGE_KEYS.CUSTOM_FIELDS)) {
      this.locationData = this.storage.get(STORAGE_KEYS.CUSTOM_FIELDS) as CustomFields;
      this.webexService.setLocationData(this.locationData);
    }
  }

  storePnrDetails(): void {
    this.storage.set(STORAGE_KEYS.PNR_DETAILS, this.pnrDetails);
  }

  removePnrDetails(): void {
    this.pnrDetails = { pnr_no: '', last_name: '' };
    this.storage.remove(STORAGE_KEYS.PNR_DETAILS);
  }

  isPnrDetailsAvailable(paramsMap) {
    let params = paramsMap.params;
    if (this.storage.has(STORAGE_KEYS.PNR_DETAILS)) {
      this.pnrDetails = this.storage.get(STORAGE_KEYS.PNR_DETAILS);
      this.shouldSubmitPNR = false;
    }
    if (paramsMap && Object.keys(params).length) {
      if (params[PARAMS.PNR]) {
        this.pnrDetails.pnr_no = params[PARAMS.PNR]
      }
      if (params[PARAMS.LAST_NAME]) {
        this.pnrDetails.last_name = params[PARAMS.LAST_NAME];
      }
    }
  }

  /**
   *
   * Checks to see if we already have any location data available at all, in params or in local storage
   *
   */
  isLocationAvailable(paramsMap) {

    let isLocationAvailable = true;

    if (this.isLocationAvailableInService()) { }
    else if (this.isLocationInParams(paramsMap)) {
      this.isLocationAvailableInParams = true;
    }
    else {
      isLocationAvailable = false;
    }


    // if we got a location, let's proceed with setting it up
    if (isLocationAvailable) {
      this.webexService.setLocationData(this.locationData);
    }
    this.isPnrDetailsAvailable(paramsMap);
    this.logger.debug("Session Service: isLocationAvailable: ", isLocationAvailable);
    return isLocationAvailable;
  }

  /**
   *
   * Checks to see if we already have any location data available at all, in params or in local storage
   *
   */
  isUserInfoAvailable() {
    this.logger.debug("Session Service: isUserInfoAvailable: ", this.userData);

    // get session storage
    if (this.storage.has(STORAGE_KEYS.USER_DETAILS)) {
      this.userData = this.storage.get(STORAGE_KEYS.USER_DETAILS);
    }

    return (
      this.userData.firstName &&
      this.userData.lastName &&
      this.userData.phone &&
      this.userData.connectionType
    );
  }

  isLocationAvailableInService() {
    let isLocationAvailable = this.locationData["airport-code"] || this.locationData["routing-code"];
    return isLocationAvailable && isLocationAvailable !== "";
  }

  /**
   *
   * Checks if location is in params
   * @returns - true/false based on whether the location has been found in url params
   *
   */
  isLocationInParams(paramsMap) {

    let params: { [key: string]: string } = {}

    if (paramsMap && paramsMap.keys.length) {
      this.logger.debug("Session Service: isLocationInParams in url: ", paramsMap);

      if (paramsMap.has(PARAMS.ENCRYPT_STRING)) {
        this.generateEncryptedString(paramsMap)
      }

      if (paramsMap.has(PARAMS.ENCRYPTED_STRING)) {
        try {
          this.encryptedParam = paramsMap.get(PARAMS.ENCRYPTED_STRING);
          this.encryptedParam = decodeURI(this.encryptedParam).replace(/\s/g, '+'); //handle encode issue with '+' and ' '
          let paramString = this.cryptoService.decrypt(this.encryptedParam); //decode encrypted json string
          if (paramString) paramsMap.params = JSON.parse(paramString);
          this.isEncryptedStringInParams = true;
        }
        catch (error) {
          this.logger.warn("Session Service: isLocationInParams in session storage: ", error);
        }
      }

      if (paramsMap.has(PARAMS.GATE)) params[PARAMS.GATE] = paramsMap.get(PARAMS.GATE);
      if (paramsMap.has(PARAMS.AIRPORT)) params[PARAMS.AIRPORT] = paramsMap.get(PARAMS.AIRPORT)?.toUpperCase();
      if (paramsMap.has(PARAMS.ROUTING_CODE)) params[PARAMS.ROUTING_CODE] = paramsMap.get(PARAMS.ROUTING_CODE)?.toUpperCase();
      if (paramsMap.has(PARAMS.ASSET_ID)) params[PARAMS.ASSET_ID] = paramsMap.get(PARAMS.ASSET_ID)?.toUpperCase()?.trim()?.replace(/[^A-Za-z0-9]/g, '');
      if (paramsMap.has(PARAMS.STATION_ID)) params[PARAMS.STATION_ID] = paramsMap.get(PARAMS.STATION_ID);
      if (paramsMap.has(PARAMS.UA_SOURCE)) params[PARAMS.UA_SOURCE] = paramsMap.get(PARAMS.UA_SOURCE);
      if (paramsMap.has(PARAMS.REASON)) params[PARAMS.REASON] = paramsMap.get(PARAMS.REASON);
      if (paramsMap.has(PARAMS.LAST_NAME)) params[PARAMS.LAST_NAME] = paramsMap.get(PARAMS.LAST_NAME);
      if (paramsMap.has(PARAMS.PNR)) params[PARAMS.PNR] = paramsMap.get(PARAMS.PNR);



      if (paramsMap.has(PARAMS.ENCRYPTED_STRING)) this.encryptedParam = paramsMap.get(PARAMS.ENCRYPTED_STRING);

      // this.storage.clear();
      this.storage.set(STORAGE_KEYS.PARAMS, params);

    } else if (this.storage.has(STORAGE_KEYS.PARAMS)) {
      this.logger.debug("Session Service: isLocationInParams in session storage: ", paramsMap);
      params = this.storage.get(STORAGE_KEYS.PARAMS);

      /* -- only boarding pass reason to be used on reloads, remove when not needed.--*/
      if (params[PARAMS.REASON] != HELP_OPTION_CODES.BOARDING_PASS) {
        delete params[PARAMS.REASON];
        this.storage.set(STORAGE_KEYS.PARAMS, params);
      }

      if (ENVIRONMENT.OUTPUT) {
        this.logger.debug('Session Service: Session storage params ', this.storage.get(STORAGE_KEYS.PARAMS))
      }
    }

    const isIt = this.checkParams(params);
    this.logger.debug("Session Service: isLocationInParams: ", isIt);

    return isIt;
  }

  /**
   *
   * Parses parameters and stores in service
   *
   */
  checkParams(params) {
    this.logger.debug("Session Service: checkParams: ", params);

    if (!Object.keys(params).length) return false

    const gateParam = params[PARAMS.GATE];
    const airportCodeParam = params[PARAMS.AIRPORT];
    const routingCodeParam = params[PARAMS.ROUTING_CODE];
    const assetIdParam = params[PARAMS.ASSET_ID];
    const stationIdParam = params[PARAMS.STATION_ID];
    const uaSourceParam = params[PARAMS.UA_SOURCE];
    const reasonParam = params[PARAMS.REASON];

    if (uaSourceParam && Object.values(UA_SOURCE_CODES).find(c => c.toLowerCase() == uaSourceParam.toLowerCase())) {
      this.locationData["ua_source"] = uaSourceParam;
    }

    if (airportCodeParam || routingCodeParam) {

      setTimeout(() => {
        if (airportCodeParam) {
          const airport = airportCodeParam.toUpperCase();
          this.setAirportCode(airport)
        }
        if (gateParam) {
          const gate = gateParam.toUpperCase();
          this.locationData["gate-number"] = gate;
        }
        if (stationIdParam) {
          const stationId = stationIdParam;
          this.locationData.stationId = stationId;
        }
        if (assetIdParam) {
          const assetId = assetIdParam.toUpperCase();
          this.locationData["asset-id"] = assetId;
        }
        if (routingCodeParam) {
          this.locationData["routing-code"] = routingCodeParam.toUpperCase();
          const routingCode = routingCodeParam.toUpperCase();
          // this.locationData["rou-code"] = routingCode;
          // this.locationData["routing-code"] = "abc";
          // this.locationData["mp-number"] = "abc";
          // this.locationData = { ...this.locationData, "routing-code": routingCode}
        }
        if (reasonParam) {
          this.locationData.reason = reasonParam.toLowerCase();
        }
        if (ENVIRONMENT.OUTPUT) {
          this.logger.debug('Session service: locationData ', this.locationData);
        }
      }, 2000)


      return true
    }

    return false
  }

  /**
   * Sets routing code in the custom fields
   * @param routingCode
   */
  setRoutingCode(routingCode: string) {
    this.locationData["routing-code"] = routingCode;
    this.logger.debug("Session Service: setRoutingCode: ", this.locationData);
  }

  /**
   * Sets Airport code in the custom fields
   * @param airportCode
   */
  setAirportCode(airportCode: string) {
    this.locationData["airport-code"] = airportCode;
    this.logger.debug("Session Service: setAirportCode: ", this.locationData);
  }

  setSmsConsent(smsConsent: string) {
    this.locationData['sms_consent'] = smsConsent;
    this.logger.debug("Session Service: smsConsent: ", this.locationData);
  }
  /**
   *
   * Sets custom region once it is known that it is ready
   *
   * @param region - The string id of the region to track
   *
   */
  setRegion(region: string) {
    this.locationData.region = region.toLowerCase();
    this.logger.debug("Session Service: setRegion: ", this.locationData);
  }

  /**
   * 
   *  sets the default language to the location data or the language sent throut param.
   */
  setLanguage(language: string) {
    this.locationData["language"] = language;
    this.logger.debug("Session Service: setLanguage: ", this.locationData);
  }

  /**
   * Sets Flight Number in the custom fields
   * @param flightNumber
   */
  setFlightNumber(flightNumber: string) {
    this.locationData["flight_no"] = flightNumber;
    this.logger.debug("Session Service: setFlightNumber: ", this.locationData);
  }

  /**
   * Sets PNR in the custom fields
   * @param pnr
   */
  setPNR(pnr: string) {
    this.locationData.pnr_no = pnr;
    this.logger.debug("Session Service: setPNR: ", this.locationData);
  }

  /**
   * Sets Origin Airport in the custom fields
   * @param originAirport
   */
  setOriginAirport(originAirport: string) {
    this.locationData["origin_airport"] = originAirport;
    this.logger.debug("Session Service: setDestinationAirport: ", this.locationData);
  }

  /**
   * Sets Destination Airport in the custom fields
   * @param destinationAirport
   */
  setDestinationAirport(destinationAirport: string) {
    this.locationData["destination-airport"] = destinationAirport;
    this.logger.debug("Session Service: setDestinationAirport: ", this.locationData);
  }

  /**
   * Sets Priority Score in the custom fields
   * @param priorityScore
   */
  setPriorityScore(priorityScore: string) {
    this.locationData.priority_score = priorityScore;
    this.logger.debug("Session Service: setPriorityScore: ", this.locationData);
  }

  /**
   * Sets Key Attributes in the custom fields
   * @param keyAttributes
   */
  setKeyAttributes(keyAttributes: any) {
    let key_attributes = '';
    if (keyAttributes) {
      keyAttributes.some(attribute => {
        key_attributes += `${attribute.ruleCode},`;
      })
      key_attributes = key_attributes.slice(0, -1);
    }
    this.locationData.key_attributes = key_attributes;
    this.logger.debug("Session Service: setKeyAttributes: ", this.locationData);
  }

  /**
   * Sets Departure Date in the custom fields
   * @param destinationAirport
   */
  setDepartureDate(destinationAirport: string) {
    this.locationData.departure_date = destinationAirport;
    this.logger.debug("Session Service: setDepartureDate: ", this.locationData);
  }

  /**
   * Sets Is Checked In in the custom fields
   * @param isCheckedIn
   */
  setIsCheckedIn(isCheckedIn: string) {
    this.locationData.is_checked_in = isCheckedIn;
    this.logger.debug("Session Service: setIsCheckedIn: ", this.locationData);
  }

  /**
   * Sets routed department id in the custom fields
   * @param isCheckedIn
   */
  setRoutedDepartmentId(routedDepartmentId: string) {
    this.logger.debug('Session Service: setRoutedDepartmentId()', routedDepartmentId);
    this.locationData["routed-department-id"] = routedDepartmentId || "";
  }

  /**
 * Sets current location in the custom fields
 * @param location
 */
  setCurrentLocation(location: string) {
    this.logger.debug('Session Service: setCurrentLocation()', location);
    this.locationData["currentLocation"] = location.substring(location.length - 3) || "";
  }

  /**
   * Sets the custom message from the routing API to session service.
   * @param message 
   */
  setCustomAutoMessage(message: string) {
    this.logger.debug('Session Service: setCustomAutoMessage()', message);
    this.locationData["auto-message"] = message?.replace(/\r?\n|\r/g, " ");
  }

  /**
 * clears the custom message from the user fields
 * @param message 
 */
  clearCustomAutoMessage() {
    this.logger.debug('Session Service: clearCustomAutoMessage()');
    this.locationData["auto-message"] = ''
  }

  /**
   * Sets the referring component in the session service.
   * @param component 
   */
  setReferrer(component: string) {
    this.logger.debug('Session Service: setReferrer()', component)
    this.locationData.referrer = component
  }

  /**
 * Sets the mpNumber in the session service.
 * @param component 
 */
  setmpNumber(number: string) {
    this.logger.debug('Session Service: setmpNumber()', number)
    this.locationData["mp-number"] = number;
  }


  /**
   * sets cookie-consent to session storage
   * @returns void
   */
  storeCookieConsent(value: boolean = true): void {
    this.logger.debug("Session Service: storeCookieConsent: ", value);
    this.localStorage.set(STORAGE_KEYS.COOKIE_CONSENT, value);
  }

  /**
   * gets cookie-consent from session storage
   * @returns boolean
   */
  loadCookieConsentFromStorage() {
    let cookieConsent = this.localStorage.get(STORAGE_KEYS.COOKIE_CONSENT);
    return cookieConsent && true;
  }

  /**
  * gets languagePreferred from local storage
  */
  loadLanguagePreferredFromStorage() {
    let langConsent = this.localStorage.get(STORAGE_KEYS.LANGUAGE);
    langConsent && (this.isLangPreferred = true);
    return langConsent || false;
  }

  /**
  * sets languagePreferred to local storage
  */
  storeLanguagePreferred(value: string): void {
    this.localStorage.set(STORAGE_KEYS.LANGUAGE, value);
    this.isLangPreferred = true;
    this.logger.debug('session Service : storeLanguageConsent');
  }
  removeLanguagePreferred() {
    this.localStorage.remove(STORAGE_KEYS.LANGUAGE);
    this.isLangPreferred = false;
  }

  /**
   * checks if the app flow is integrated and reaches pre-chat
   */
  isIntegratedFlow(sourceCodes = [UA_SOURCE_CODES.dotCom, UA_SOURCE_CODES.baggage]) {
    return sourceCodes.some((sourceCode) => sourceCode.toLowerCase() == this.locationData.ua_source.toLowerCase());
  }

  /**
   * Return true when reason from params had boarding-pass
   * This is a closure, the inner method can be used anytime to determine what the initial reason was
   * @returns function
   */

  isBoardingPassFlow(reasonCode: string): Function {
    let isBoardingPass = false;
    if (reasonCode == HELP_OPTION_CODES.BOARDING_PASS) isBoardingPass = true;
    return (): boolean => isBoardingPass;
  }

  /**
   * Clear the flight rebook current component when it gets out of scope
   */
  clearFlightRebookCurrentComponent() {
    this.flightRebookCurrentComponent = '';
  }

  /**
   * generates an encrypted string using the default aod client key
   * @param paramMap
   * @returns void
   */
  generateEncryptedString(paramsMap: ParamMap): void {
    this.logger.warn("SessionService.generateEncryptedString()", paramsMap);
    try {
      let encryptedParams: ObjectLiteral = paramsMap.keys.reduce((a, c) => {
        a[c] = paramsMap.get(c)
        return a
      }, {})
      let paramString = this.cryptoService.encrypt(encryptedParams)
    } catch (error) {
      this.logger.warn("SessionService.generateEncryptedString()", error);
    }
  }

}


export const SESSION_STORAGE_SERVICE = new InjectionToken<StorageService>('SESSION_STORAGE_SERVICE');
export const LOCAL_STORAGE_SERVICE = new InjectionToken<StorageService>('LOCAL_STORAGE_SERVICE');

export const SessionStorageProvider = {
  provide: SESSION_STORAGE_SERVICE,
  useExisting: SESSION_STORAGE,
}

export const LocalStorageProvider = {
  provide: LOCAL_STORAGE_SERVICE,
  useExisting: LOCAL_STORAGE,
}